<template>
    <div ref="dropdown" v-if="isFormReady">
        <div v-if="!selectedOption" class="form-select" :class="classes.wrapper">
            <input
                ref="search"
                :disabled="disabled"
                :class="[classes.input, isRequired]"
                :placeholder="placeholder"
                :required="required"
                :readonly="disabled"
                @focus="seedSearchText()"
                @keydown.enter.prevent="setOption()"
                @keyup.enter.prevent="setOption()"
                @keyup.down="movePointerDown()"
                @keyup.tab.stop="closeOut()"
                @keyup.esc.stop="closeOut()"
                @keyup.up="movePointerUp()"
                autocomplete="off"
                v-model="searchText"
            />

            <div
                @click="toggleDropdown"
                :class="[classes.icons, isDisabled]"
                class="icon"
            >
                <b-icon icon="chevron-down" @click="closeOut()"></b-icon>
            </div>

            <div
                tabindex="-1"
                ref="options"
                v-if="matchingOptions && matchingOptions.length > 0"
                class="dropdown"
                :class="[classes.dropdown]"
            >
                <span
                    tabindex="-1"
                    v-for="(option, index) in matchingOptions"
                    :key="index"
                    :class="index === pointer ? 'active' : ''"
                    @mouseover="setPointerIndex(index)"
                    @keyup.enter="setOption()"
                    @keyup.up="movePointerUp()"
                    @keyup.down="movePointerDown()"
                    @click.prevent="setOption()"
                >
                    <slot name="option" v-bind="{option, index}">{{ getOptionDescription(option[optionKey]) }}</slot>
                </span>
            </div>
        </div>

        <div :class="classes.wrapper" class="form-select" v-if="selectedOption">
            <input
                ref="match"
                readonly
                :class="[classes.input]"
                :required="required"
                :value="getOptionDescription(selectedOption)"
            />
            <input type="hidden" :name="name" ref="selectedValue" :value="getOptionValue(selectedOption)" />

            <div
                :class="[classes.icons, isDisabled]"
                class="icon"
            >
                <b-icon icon="x" @click="closeOut()"></b-icon>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        value: {
            required: true
        },
        name: {
            type: String,
            required: false,
            default: () => ''
        },
        options: {
            type: Array,
            required: false,
            default: () => []
        },
        optionLabel: {
            type: String,
            required: false,
            default: () => 'name'
        },
        optionKey: {
            type: String,
            required: false,
            default: () => 'id'
        },
        placeholder: {
            type: String,
            required: false,
            default: () => ''
        },
        classes: {
            type: Object,
            required: false,
            default: () => null
        },
        initial: {
            type: String,
            required: false,
            default: () => null
        },
        disabled: {
            type: Boolean,
            required: false,
            default: () => false
        },
        required: {
            type: Boolean,
            required: false,
            default: () => false
        },
        tabindex: {
            type: String,
            required: false,
            default: () => {
                return '';
            }
        },
        getOptionDescription: {
            type: Function,
            default: function (option) {
                const selection = this.options.filter(x => x[this.optionKey] == option);

                if (selection) {
                    return selection[0][this.optionLabel];
                }

                return option;
            }
        },
        getOptionValue: {
            type: Function,
            default: function (option) {
                const selection = this.options.filter(x => x[this.optionKey] == option);

                if (selection) {
                    return selection[0][this.optionKey];
                }

                return option;
            }
        },
        filterBy: {
            type: Function,
            default: function (option) {
                if (this.optionLabel) {
                    return option[this.optionLabel]
                        .toString()
                        .toLowerCase()
                        .includes(this.searchText.toString().toLowerCase());
                }

                return option
                        .toString()
                        .toLowerCase()
                        .includes(this.searchText.toString().toLowerCase());
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('keyup', this.handleClickOutside);
        
        if (this.value && this.options.includes(this.value)) {
            this.selectedOption = this.value;
            return;
        }

        this.searchText = this.initial;
        this.setInitSelectedOption();
    },
    destroyed() {
        document.removeEventListener('keyup', this.handleClickOutside);
        document.removeEventListener('click', this.handleClickOutside);
    },
    data() {
        return {
            isFormReady: false,
            searchReady: false,
            searchText: null,
            selectedOption: null,
            dropdownOpen: false,
            pointer: -1
        };
    },
    watch: {
        value(curr) {
            this.selectedOption = curr;
        },
        options() {
            this.setInitSelectedOption();
        },
        searchText(curr, prev) {
            if (curr !== prev) {
                this.pointer = -1;
            }
        },
        selectedOption(curr) {
            this.$emit('input', curr);
            this.$emit('change', curr);
        },
        dropdownOpen(curr, prev) {
            if (curr === prev) {
                return;
            }
            if (!curr) {
                this.searchText = null;
                return;
            }
            if (!this.searchText) {
                this.searchText = '';
            }
        },
        pointer() {
            this.maybeAdjustScroll();
        }
    },
    computed: {
        isRequired() {
            if (!this.required) {
                return '';
            }

            if (this.selectedOption) {
                return '';
            }

            return 'required';
        },
        isDisabled() {
            if (!this.disabled) {
                return '';
            }

            return 'disabled';
        },
        matchingOptions() {
            if (!this.searchReady || this.searchText === null) {
                return null;
            }

            if (!this.searchText.length) {
                return [...this.options];
            }

            return this.options.filter(option => this.filterBy(option));
        }
    },
    methods: {
        setPointerIndex(index) {
            this.pointer = index;
        },
        seedSearchText() {
            if (this.disabled) {
                return;
            }

            this.searchReady = true;

            if (this.searchText !== null) {
                return;
            }

            this.searchText = '';
        },
        toggleDropdown() {
            if (this.disabled) {
                return;
            }

            this.dropdownOpen = !this.dropdownOpen;
        },
        closeOut() {
            if (this.disabled) {
                return;
            }

            this.selectedOption = null;
            this.dropdownOpen = false;
            this.searchText = null;
        },
        movePointerDown() {
            if (!this.matchingOptions) {
                return;
            }
            if (this.pointer >= this.matchingOptions.length - 1) {
                return;
            }

            this.pointer++;
        },
        movePointerUp() {
            if (this.pointer > 0) {
                this.pointer--;
            }
        },
        setInitSelectedOption() {
            let item = -1;

            for (var index = 0; index < this.options.length; index++) {
                if (this.value) {
                    if (this.options[index][this.optionKey] == this.value) {
                        item = index;
                    }
                }
            }

            if (item > -1) {
                this.searchReady = true;
                this.pointer = item;
                this.searchText = '';
                this.setOption();
            }

            this.isFormReady = true;
        },
        setOption() {
            if (!this.matchingOptions || !this.matchingOptions.length) {
                return;
            }

            if (this.pointer === -1) {
                this.pointer++;
            }

            this.selectedOption = this.matchingOptions[this.pointer][this.optionKey];

            this.searchText = null;
            this.dropdownOpen = false;
            this.pointer = -1;
        },
        handleClickOutside(e) {
            if (this.$el.contains(e.target)) {
                return;
            }

            this.dropdownOpen = false;
            this.searchText = null;
        },
        maybeAdjustScroll () {
            let pixelsToPointerTop = this.pixelsToPointerTop();
            let pixelsToPointerBottom = this.pixelsToPointerBottom();
    
            if (pixelsToPointerTop <= this.viewport().top) {
                return this.scrollTo(pixelsToPointerTop);
            }
            else if (pixelsToPointerBottom >= this.viewport().bottom) {
                return this.scrollTo(this.viewport().top + this.pointerHeight());
            }
        },
        pixelsToPointerTop() {
            let pixelsToPointerTop = 0;

            if(!this.$refs.options) {
                return 0;
            }

            for (let i = 0; i < this.pointer; i++) {
                pixelsToPointerTop += this.$refs.options.children[i].offsetHeight;
            }
            
            return pixelsToPointerTop;
        },
        pixelsToPointerBottom() {
            return this.pixelsToPointerTop() + this.pointerHeight();
        },
        pointerHeight() {
            let element = this.$refs.options ? this.$refs.options.children[this.pointer] : false;

            return element ? element.offsetHeight : 0;
        },
        viewport() {
            return {
                top: this.$refs.options ? this.$refs.options.scrollTop: 0,
                bottom: this.$refs.options ? this.$refs.options.offsetHeight + this.$refs.options.scrollTop : 0
            }
        },
        scrollTo(position) {
            return this.$refs.options ? this.$refs.options.scrollTop = position : null;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '@/Variable.scss';

.form-select {
    position: relative;

    input {
        padding-right: 35px;

        &:read-only {
            cursor: default !important;
        }
    }

    .icon {
        position: absolute;
        z-index: 0;
        right: 10px;
        top: 0;
        bottom: 0;
        height: 100%;
        cursor: pointer;
        display: flex;
        align-items: center;

        &.disabled {
            cursor: not-allowed;
        }
    }

    .dropdown {
        position: absolute;
        background: $inputBackgroundColor;
        margin: 0;
        margin-top: 1px;
        padding: 0;
        z-index: 1001;
        width: 100%;
        max-height: 300px;
        overflow: auto;
        @include boxShadow(0 0 5px rgba(0, 0, 0, 0.8));

        span {
            display: block;
            cursor: pointer;
            font-size: 15px;
            color: $textMainColor;
            padding: 10px;
            @include transition(none);

            &:hover, &.active {
                color: #fff;
                background: #3a9ed0;
            }
        }
    }
}
</style>